import { common, grey, red, lightBlue } from "@mui/material/colors"

function isDarkMode(theme) {
  return theme.palette.mode === "dark"
}

const lightPalette = {
  mode: "light",
  common: { background: common.white, onBackground: common.black },
  primary: {
    main: "#03a9f4",
    light: "rgb(230, 246, 254)",
    contrastText: common.white,
  },
  success: { main: "#4caf50", light: "#f0f8e5" },
  highlight: { bg: "#ffc" },
  secondary: { main: "#607d8b" },
  background: { default: "#ffffff", paper: "#ffffff" },
  text: {
    primary: "#333333",
    secondary: "rgba(0, 0, 0, 0.7)",
    disabled: "rgba(0, 0, 0, 0.38)",
  },
  divider: "rgba(0, 0, 0, 0.12)",
  warning: { main: "#ed6c02", light: "#fff4e5" },
  error: { main: "#d32f2f", light: red[400], dark: red[800] },
  info: { main: lightBlue[700], light: lightBlue[500], dark: lightBlue[900] },
  grey,
  action: { hover: "#f5f5f5", selected: "rgba(0, 0, 0, 0.08)" },
  chart: {
    total: "#00abe8",
    factor: "#9067a7",
    security: "#7293cb",
    alpha: "#627b8c",
    trading: "#da7c30",
    riskFree: "#2e8b57",
    series: [
      "#da7c30", "#396ab1", "#3e9651", "#cc2529", "#535154",
      "#6b4c9a", "#922428", "#948b3d", "#9b3d70", "#237994",
      "#FFCF0D", "#19FFE7", "#E51BF5", "#74F50F", "#015175",
    ],
  },
}

const darkPalette = {
  mode: "dark",
  common: { background: common.black, onBackground: common.white },
  primary: {
    main: "#03a9f4",
    light: "rgba(59, 130, 246, 0.12)",
    contrastText: common.white,
  },
  success: { main: "#14b8a6", light: "rgba(20, 184, 166, 0.15)" },
  highlight: { bg: "rgba(255, 235, 59, 0.25)" },
  secondary: { main: "#94a3b8" },
  background: { default: "#121212", paper: "#1e1e1e" },
  text: {
    primary: "#f0f0f0",
    secondary: "rgba(255, 255, 255, 0.72)",
    disabled: "rgba(255, 255, 255, 0.38)",
  },
  divider: "rgba(255, 255, 255, 0.09)",
  warning: { main: "#f59e0b", light: "rgba(245, 158, 11, 0.15)" },
  error: { main: "#e07a95", light: "#f0a5b8", dark: "#c76570" },
  info: { main: "#14b8a6", light: "#5eead4", dark: "#0d9488" },
  grey,
  action: { hover: "#323232", selected: "rgba(255, 255, 255, 0.08)" },
  chart: {
    total: "#3b82f6",
    factor: "#a78bfa",
    security: "#94a3b8",
    alpha: "#64748b",
    trading: "#f59e0b",
    riskFree: "#2dd4bf",
    series: [
      "#3b82f6", "#a78bfa", "#64748b", "#2dd4bf", "#e07a95",
      "#94a3b8", "#c084fc", "#5eead4", "#f0a5b8", "#67e8f9",
      "#fbbf24", "#a5f3fc", "#c4b5fd", "#6ee7b7", "#f9a8d4",
    ],
  },
}

// Palette values are exposed as CSS variables (--mui-palette-*) when cssVariables: true.
// Base light/dark provide most coloring; alternates only override Figma-specified keys (primary,
// secondary, background, text, divider, action hover/selected, chart, feedback) and inherit the rest.
// Keep in sync with styles/_theme-palette.scss for non-MUI overrides.
export default {
  cssVariables: true,
  colorSchemeSelector: "data-mui-color-scheme",
  colorSchemes: {
    light: { palette: lightPalette },
    dark: { palette: darkPalette },
  },
  components: {
    MuiButton: {
      color: "inherit",
      styleOverrides: {
        root: ({ theme }) => ({
          "&:not(.MuiButton-contained)": {
            color: "inherit",
          },
          "&.MuiButton-outlined:not(.MuiButton-colorPrimary):hover, &.MuiButton-text:not(.MuiButton-colorPrimary):hover":
            {
              backgroundColor: isDarkMode(theme)
                ? theme.palette.action?.hover
                : "rgba(0, 0, 0, 0.06)",
            },
        }),
      },
    },
    MuiButtonBase: {
      disableRipple: true,
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: "var(--app-modal-backdrop)",
        },
      },
    },
    MuiCardActions: {
      styleOverrides: {
        root: {
          paddingTop: 12,
          paddingBottom: 8,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: ({ theme }) =>
          isDarkMode(theme)
            ? { "&.MuiChip-filled": { backgroundColor: "rgba(244, 248, 255, 0.12)" } }
            : {},
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          "&:not(.Mui-error)": {
            color: "var(--mui-palette-text-secondary)",
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          "& input": {
            color: "var(--mui-palette-text-primary)",
          },
        },
      },
    },
    MuiListSubheader: {
      styleOverrides: {
        root: {
          backgroundColor: "var(--mui-palette-background-paper)",
          color: "var(--mui-palette-text-primary)",
          ".MuiPopover-root &": { backgroundColor: "transparent" },
          ".MuiMenu-root &": { backgroundColor: "transparent" },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: "var(--mui-palette-text-primary)",
          minWidth: 160,
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          margin: 0,
          backgroundColor: "var(--app-bg-body)",
        },
      },
    },
  },
  typography: {
    fontSize: 16,
    fontFamily: ['"Open Sans"', '"Helvetica"', '"Arial"', "sans-serif"].join(","),
    fontWeightMedium: 600,
    useNextVariants: true,
    body1: {
      fontFamily: ['"Open Sans"', '"Helvetica"', '"Arial"', "sans-serif"].join(","),
    },
  },
}
